import { useCallback, useEffect, useMemo, useState } from "react";
import {
  approveLabFeedbackItem,
  fetchLabFeedback,
  saveLabFeedback,
  sendLabFeedback,
  UnauthorizedError,
  type ClinicianLabFeedback,
  type ClinicianLabFeedbackApprovalInput,
} from "@/lib/clinician-api";
import {
  analyteName,
  BUCKET_BADGE_CLASS,
  BUCKET_LABEL,
  formatRange,
  formatValue,
  labFeedbackErrorMessage,
} from "@/lib/lab-labels";
import {
  completenessLabel,
  draftLabel,
  FEEDBACK_ITEM_STATE_LABEL,
  feedbackItemState,
  isLegacySent,
  publishReadiness,
  VALUE_PUBLISH_STATE_LABEL,
  valuePublishState,
  type FeedbackItemState,
} from "@/lib/lab-feedback-review";
import { useReadOnly } from "./SupportView";

// TES-201 — the doctor's lab-feedback surface (within the patient detail's
// "Labbprover" section, rendered once per order with values).
//
// TES-1370 (Ö-582) — review, approve item by item, then publish. Emelie:
// "Behörig läkare granskar, ändrar och godkänner uttryckligen allt före
// publicering." Each value shows its result, the lab's own comment (for the
// doctor only, never published), the template draft and his explanation;
// each explanation and the summary is approved on its own, and "Publicera"
// is enabled only when everything written is approved as it stands (the
// server refuses otherwise). A partial result is marked "Delsvar" and may be
// published as one.

const SUMMARY_MAX = 1000;
const COMMENT_MAX = 500;

function fmt(date: string) {
  return new Date(date).toLocaleString("sv-SE", {
    dateStyle: "short",
    timeStyle: "short",
  });
}

const STATE_CHIP_CLASS: Record<FeedbackItemState, string> = {
  empty: "bg-surfaceMuted text-textSecondary border-surfaceMuted",
  approved: "bg-success/10 text-success border-success/30",
  edited_since_approval: "bg-warning/10 text-warning border-warning/40",
  unapproved: "bg-warning/10 text-warning border-warning/40",
};

function StateChip({ state }: { state: FeedbackItemState }) {
  return (
    <span
      className={`text-[11px] px-xs py-[2px] rounded-pill border ${STATE_CHIP_CLASS[state]}`}
    >
      {FEEDBACK_ITEM_STATE_LABEL[state]}
    </span>
  );
}

// The template draft beside the doctor's field. It never becomes his text by
// itself: "Använd utkastet" copies it into the field to edit, "Godkänn
// utkastet" approves it exactly as written.
function DraftBox({
  label,
  text,
  disabled,
  onUse,
  onApprove,
}: {
  label: string;
  text: string | null;
  disabled: boolean;
  onUse: (text: string) => void;
  onApprove: ((text: string) => void) | null;
}) {
  if (!text) return null;
  return (
    <div className="mt-xs rounded-sm bg-surfaceMuted/40 border border-surfaceMuted p-sm">
      <p className="text-[11px] text-textSecondary mb-[2px]">{label}</p>
      <p className="text-xs text-textSecondary italic">{text}</p>
      {disabled ? null : (
        <div className="flex gap-sm mt-xs">
          <button
            type="button"
            onClick={() => onUse(text)}
            className="text-xs text-brand hover:underline"
          >
            Använd utkastet
          </button>
          {onApprove ? (
            <button
              type="button"
              onClick={() => onApprove(text)}
              className="text-xs text-brand hover:underline"
            >
              Godkänn utkastet som det är
            </button>
          ) : null}
        </div>
      )}
    </div>
  );
}

function ApproveButtons({
  state,
  busy,
  onApprove,
  onWithdraw,
}: {
  state: FeedbackItemState;
  busy: boolean;
  onApprove: () => void;
  onWithdraw: () => void;
}) {
  if (state === "empty") return null;
  if (state === "approved") {
    return (
      <button
        type="button"
        disabled={busy}
        onClick={onWithdraw}
        className="text-xs text-textSecondary hover:underline disabled:opacity-50"
      >
        Ångra godkännande
      </button>
    );
  }
  return (
    <button
      type="button"
      disabled={busy}
      onClick={onApprove}
      className="px-sm py-[2px] rounded-sm border border-success/40 text-success text-xs hover:bg-success/10 disabled:opacity-50"
    >
      Godkänn
    </button>
  );
}

interface Props {
  patientId: string;
  orderId: string;
  // Called after a successful publish so the parent can refresh (the
  // order's reviewedAt badge flips to "Godkänd" for a complete result).
  onSent?: () => void;
  onUnauthorized?: () => void;
}

export function LabFeedbackPanel({
  patientId,
  orderId,
  onSent,
  onUnauthorized,
}: Props) {
  // TES-1040 — the admin's read-only view: the feedback as it stands, no
  // editing, approving or publishing.
  const readOnly = useReadOnly();
  const [feedback, setFeedback] = useState<ClinicianLabFeedback | null>(null);
  const [summary, setSummary] = useState("");
  // Per-analyte comment edits, keyed by analyteKey.
  const [comments, setComments] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Seed local edit state from a freshly-fetched (or freshly-written) panel.
  const seed = useCallback((fb: ClinicianLabFeedback) => {
    setFeedback(fb);
    setSummary(fb.patientSummary);
    setComments(
      Object.fromEntries(fb.results.map((r) => [r.analyteKey, r.comment])),
    );
  }, []);

  useEffect(() => {
    let active = true;
    setLoading(true);
    fetchLabFeedback(patientId, orderId)
      .then((fb) => {
        if (active) seed(fb);
      })
      .catch((e) => {
        if (!active) return;
        if (e instanceof UnauthorizedError) {
          onUnauthorized?.();
          return;
        }
        setError(
          e instanceof Error ? labFeedbackErrorMessage(e.message) : String(e),
        );
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [patientId, orderId, seed, onUnauthorized]);

  const input = useMemo(
    () => ({
      patientSummary: summary,
      comments: (feedback?.results ?? []).map((r) => ({
        analyteKey: r.analyteKey,
        comment: comments[r.analyteKey] ?? "",
      })),
    }),
    [summary, comments, feedback],
  );

  const readiness = useMemo(
    () => (feedback ? publishReadiness(feedback, { summary, comments }) : null),
    [feedback, summary, comments],
  );

  async function run(key: string, fn: () => Promise<ClinicianLabFeedback>) {
    setBusy(key);
    setError(null);
    try {
      seed(await fn());
      return true;
    } catch (e) {
      if (e instanceof UnauthorizedError) {
        onUnauthorized?.();
        return false;
      }
      setError(
        e instanceof Error ? labFeedbackErrorMessage(e.message) : String(e),
      );
      return false;
    } finally {
      setBusy(null);
    }
  }

  // Approving (or withdrawing) one item re-reads the whole panel, so any
  // other unsaved edit is saved first rather than lost. Saving an edited
  // text withdraws its earlier approval on the server, which is the point.
  function approval(key: string, body: ClinicianLabFeedbackApprovalInput) {
    return run(key, async () => {
      if (readiness?.dirty) await saveLabFeedback(patientId, orderId, input);
      return approveLabFeedbackItem(patientId, orderId, body);
    });
  }

  async function publish() {
    if (!feedback || !readiness?.ready) return;
    const partial = completenessLabel(feedback).partial;
    const ok = window.confirm(
      partial
        ? "Publicera delsvaret för patienten? Hon ser de värden som har kommit, märkta som ofullständiga, med dina godkända texter."
        : "Publicera provsvaren för patienten? Hon ser värdena med dina godkända texter.",
    );
    if (!ok) return;
    const done = await run("publish", () =>
      sendLabFeedback(patientId, orderId, input),
    );
    if (done) onSent?.();
  }

  if (loading) {
    return <p className="text-textSecondary text-sm mt-sm">Hämtar provsvar…</p>;
  }
  if (!feedback) {
    return error ? <p className="text-danger text-sm mt-sm">{error}</p> : null;
  }

  const legacy = isLegacySent(feedback);
  const editable = !readOnly && !legacy;
  const completeness = completenessLabel(feedback);
  const summaryState = feedbackItemState({
    savedText: feedback.patientSummary,
    approvedAt: feedback.summaryApprovedAt,
    localText: summary,
  });
  const label = draftLabel(feedback);

  return (
    <div className="mt-md border-t border-surfaceMuted pt-md">
      <div className="flex flex-wrap items-center gap-sm mb-sm">
        <span
          className={`text-xs px-sm py-[2px] rounded-pill border ${
            completeness.partial
              ? "bg-warning/10 text-warning border-warning/40"
              : "bg-success/10 text-success border-success/30"
          }`}
        >
          {completeness.text}
        </span>
        {feedback.publishedAt ? (
          <span className="text-xs text-success">
            ✓ Publicerad för patienten {fmt(feedback.publishedAt)}
          </span>
        ) : legacy ? (
          <span className="text-xs text-success">
            ✓ Skickad till patient {fmt(feedback.feedbackSentAt as string)}
          </span>
        ) : (
          <span className="text-xs text-textSecondary">
            Inte publicerad för patienten
          </span>
        )}
      </div>

      <h3 className="font-display text-base mb-xs">
        Provsvar och förklaringar till patienten
      </h3>
      <p className="text-xs text-textSecondary mb-sm">
        Ingenting publiceras automatiskt. Granska och godkänn varje förklaring
        och sammanfattningen, och publicera sedan. Labbets egna kommentarer
        visas bara här och aldrig för patienten – för in innebörden i din
        förklaring när den behövs.
      </p>
      {legacy ? (
        <p className="text-xs text-textSecondary mb-sm">
          Det här provsvaret skickades före granskningsflödet och visas som det
          skickades.
        </p>
      ) : null}

      <ul className="flex flex-col gap-sm">
        {feedback.results.map((r) => {
          const range = formatRange(r.refLow, r.refHigh);
          const local = comments[r.analyteKey] ?? "";
          const state = feedbackItemState({
            savedText: r.comment,
            approvedAt: r.commentApprovedAt,
            localText: local,
          });
          const publishState = valuePublishState(r);
          const flagged = (feedback.aiDraftRiskFlags ?? []).includes(
            r.analyteKey,
          );
          const itemBusy = busy === `value:${r.analyteKey}`;
          return (
            <li
              key={r.analyteKey}
              className="rounded-sm border border-surfaceMuted p-sm"
            >
              <div className="flex flex-wrap items-center justify-between gap-sm">
                <span className="text-sm font-medium">
                  {analyteName(r.analyteKey)}
                  {flagged ? (
                    <span
                      className="ml-xs text-warning"
                      title="Utanför referensintervallet"
                    >
                      ⚠
                    </span>
                  ) : null}
                </span>
                <span className="flex items-center gap-xs">
                  {r.labPreliminary ? (
                    <span className="text-[11px] px-xs py-[2px] rounded-pill border bg-warning/10 text-warning border-warning/40">
                      Preliminärt
                    </span>
                  ) : null}
                  <span
                    className={`text-[11px] px-xs py-[2px] rounded-pill border ${
                      BUCKET_BADGE_CLASS[r.bucket]
                    }`}
                  >
                    {BUCKET_LABEL[r.bucket]}
                  </span>
                </span>
              </div>
              <div className="text-sm text-textPrimary mt-[2px]">
                {formatValue(r.value, r.valueText)} {r.unit}
                {range ? (
                  <span className="text-textSecondary text-xs ml-xs">
                    (ref {range})
                  </span>
                ) : null}
                <span className="text-textSecondary text-xs ml-sm">
                  {VALUE_PUBLISH_STATE_LABEL[publishState]}
                </span>
              </div>

              {r.labComment ? (
                <div className="mt-xs rounded-sm border border-surfaceMuted p-sm">
                  <p className="text-[11px] text-textSecondary">
                    Labbets kommentar – visas inte för patienten
                  </p>
                  <p className="text-xs whitespace-pre-wrap">{r.labComment}</p>
                </div>
              ) : null}

              {editable && !local.trim() ? (
                <DraftBox
                  label={label}
                  text={r.aiDraftComment}
                  disabled={busy !== null}
                  onUse={(text) =>
                    setComments((prev) => ({ ...prev, [r.analyteKey]: text }))
                  }
                  onApprove={(text) =>
                    void approval(`value:${r.analyteKey}`, {
                      approved: true,
                      target: "value",
                      analyteKey: r.analyteKey,
                      text,
                    })
                  }
                />
              ) : null}

              <label
                className="block text-xs font-medium text-textSecondary mt-xs"
                htmlFor={`lab-comment-${r.analyteKey}`}
              >
                Förklaring till patienten
              </label>
              <textarea
                id={`lab-comment-${r.analyteKey}`}
                className="mt-[2px] w-full border border-surfaceMuted rounded-sm p-sm text-sm min-h-[56px] focus:outline-none focus:border-brand disabled:bg-surfaceMuted/40 disabled:text-textSecondary"
                value={local}
                maxLength={COMMENT_MAX}
                disabled={!editable || busy !== null}
                onChange={(e) =>
                  setComments((prev) => ({
                    ...prev,
                    [r.analyteKey]: e.target.value,
                  }))
                }
              />
              <div className="flex items-center justify-between gap-sm mt-[2px]">
                <StateChip state={state} />
                {editable ? (
                  <ApproveButtons
                    state={state}
                    busy={busy !== null}
                    onApprove={() =>
                      void approval(`value:${r.analyteKey}`, {
                        approved: true,
                        target: "value",
                        analyteKey: r.analyteKey,
                        text: local,
                      })
                    }
                    onWithdraw={() =>
                      void approval(`value:${r.analyteKey}`, {
                        approved: false,
                        target: "value",
                        analyteKey: r.analyteKey,
                      })
                    }
                  />
                ) : null}
                {itemBusy ? (
                  <span className="text-xs text-textSecondary">Sparar…</span>
                ) : null}
              </div>
            </li>
          );
        })}
      </ul>

      <div className="mt-md rounded-sm border border-surfaceMuted p-sm">
        <label className="text-sm font-medium" htmlFor="lab-summary">
          Sammanfattning av hela provtagningen (syns för patienten)
        </label>
        {feedback.publishedSummary &&
        feedback.publishedSummary !== summary.trim() ? (
          <p className="text-xs text-textSecondary mt-[2px]">
            Patienten ser just nu: {feedback.publishedSummary}
          </p>
        ) : null}
        {editable && !summary.trim() ? (
          <DraftBox
            label={label}
            text={feedback.aiDraftSummary}
            disabled={busy !== null}
            onUse={(text) => setSummary(text)}
            onApprove={(text) =>
              void approval("summary", {
                approved: true,
                target: "summary",
                text,
              })
            }
          />
        ) : null}
        <textarea
          id="lab-summary"
          className="mt-xs w-full border border-surfaceMuted rounded-sm p-sm text-sm min-h-[96px] focus:outline-none focus:border-brand disabled:bg-surfaceMuted/40 disabled:text-textSecondary"
          value={summary}
          maxLength={SUMMARY_MAX}
          disabled={!editable || busy !== null}
          onChange={(e) => setSummary(e.target.value)}
          placeholder="Skriv en sammanfattning till patienten…"
        />
        <div className="flex items-center justify-between gap-sm mt-[2px]">
          <StateChip state={summaryState} />
          <span className="flex items-center gap-sm">
            {editable ? (
              <ApproveButtons
                state={summaryState}
                busy={busy !== null}
                onApprove={() =>
                  void approval("summary", {
                    approved: true,
                    target: "summary",
                    text: summary,
                  })
                }
                onWithdraw={() =>
                  void approval("summary", {
                    approved: false,
                    target: "summary",
                  })
                }
              />
            ) : null}
            <span className="text-xs text-textSecondary">
              {summary.length}/{SUMMARY_MAX}
            </span>
          </span>
        </div>
      </div>

      {error ? (
        <p className="text-danger text-sm mt-sm" role="alert">
          {error}
        </p>
      ) : null}

      {editable && readiness ? (
        <div className="flex flex-wrap items-center justify-end gap-sm mt-md">
          {readiness.reason ? (
            <span className="text-xs text-textSecondary mr-auto">
              {readiness.reason}
            </span>
          ) : null}
          <button
            type="button"
            onClick={() =>
              void run("save", () => saveLabFeedback(patientId, orderId, input))
            }
            disabled={!readiness.dirty || busy !== null}
            className="px-md py-sm rounded-md border border-surfaceMuted text-textSecondary text-sm hover:bg-surfaceMuted disabled:opacity-50"
          >
            {busy === "save" ? "Sparar…" : "Spara utkast"}
          </button>
          <button
            type="button"
            onClick={() => void publish()}
            disabled={!readiness.ready || busy !== null}
            className="px-md py-sm rounded-md bg-success text-white text-sm hover:opacity-90 disabled:opacity-50"
          >
            {busy === "publish"
              ? "Publicerar…"
              : completeness.partial
                ? "Publicera delsvar"
                : feedback.publishedAt
                  ? "Publicera uppdatering"
                  : "Publicera för patienten"}
          </button>
        </div>
      ) : null}
    </div>
  );
}
